import { store } from "../..";
import { ADD_NOTIFICATION } from "../../store/types";
import { useCallback, useEffect, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import PauseIcon from "@mui/icons-material/Pause";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EmailIcon from "@mui/icons-material/Email";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import TimesheetSVG from "../../assets/svgs/timesheet.svg";
import styles from "../../assets/styles/Users.module.scss";
import DeleteSVG from "../../assets/svgs/delete.svg";
import EditSVG from "../../assets/svgs/edit.svg";
import axios from "../../axios";
import PopupDialog from "../../components/popupDialog/PopupDialog";
import { ShowForPermission } from "../../components/shared/ShowForPermission";
import Button from "../../components/ui/button/Button";
import AddUser from "../../components/users/add-user/AddUser";
import { getOnlyRoles } from "../../services/role.service";
import { getFacilityOption, updateUserPauseDate } from "../../services/user.service";
import api from '../../services/api/api';
import { IconButton, InputAdornment, TextField, Switch, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Menu, MenuItem } from "@mui/material";
import PauseDateDialog from "../../components/shared/PauseDateDialog";
import { AUTH_ROLES } from "../../types/auth.type";
import { checkAiChatAccess, updateAiChatAccess, getUserUsageStats, setCustomPromptLimit } from "../../services/ai-chat-management.service";
import useAccountId from "../../hooks/useAccountId";
import { useConfirm } from "../../components/shared/dialogs";

const AccountUserPage = ({ onPauseDateSelected }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const [users, setUsers] = useState([]);
	const [showAdd, setShowAdd] = useState(false);
	const [currentEdit, setCurrentEdit] = useState(null);
	const [facilityFilter, setFacilityFilter] = useState(null);
	const [roles, setRoles] = useState([]);
	const { auth, common } = useSelector(({ auth, common }) => ({ auth, common }));
	const [facilities, setFacilities] = useState([]);
	const [deleteUserState, setDeleteUserState] = useState({ state: false, userId: null });
	const [deleteUserLoading, setDeleteUserLoading] = useState(false);
	const [actionMenuState, setActionMenuState] = useState({ anchorEl: null, user: null });
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const canPauseUsers = [AUTH_ROLES.SUPER, AUTH_ROLES.TOTAL].some(
		role => role.toUpperCase() === auth?.role?.slug?.toUpperCase()
	);

	const canManageAiChat = [AUTH_ROLES.SUPER, AUTH_ROLES.TOTAL].some(
		role => role.toUpperCase() === auth?.role?.slug?.toUpperCase()
	);

	const confirm = useConfirm();
	// Pause date dialog state
	const [pauseDialogState, setPauseDialogState] = useState({
		open: false,
		userId: null,
		userName: "",
		pauseDate: null,
		pauseType: "user" // user, account, facility
	});
	const [pauseDialogLoading, setPauseDialogLoading] = useState(false);
	const { accountId: accountIdFromHook } = useAccountId();
	// AI Chat management state
	const [aiChatStates, setAiChatStates] = useState({}); // { userId: { hasAccess, loading, usage, customLimit } }
	const [aiChatLimitDialog, setAiChatLimitDialog] = useState({
		open: false,
		userId: null,
		userName: "",
		currentLimit: null,
		newLimit: ""
	});

	useEffect(() => {
		getOnlyRoles().then((res) => {
			setRoles(res || []);
		});
	}, []);

	const getUsers = async (facilityId = null) => {
		setLoading(true);
		if (facilityId) {
			let re = await axios
				.get(`/api/user/${accountIdFromHook}`, { params: { facilityId } })
				.catch((e) => e);
			const newUsers = re.data || [];
			setUsers(newUsers);
			// Process AI Chat data immediately if available in users array
			if (canManageAiChat && newUsers.length > 0) {
				const hasAiChatData = newUsers.some(user => user.aiChatData);
				if (hasAiChatData) {
					const newStates = {};
					for (const user of newUsers) {
						if (user.aiChatData) {
							newStates[user._id] = {
								hasAccess: user.aiChatData.hasAccess || false,
								aiChatEnabled: user.aiChatData.aiChatEnabled,
								customLimit: user.aiChatData.aiChatLimit,
								usage: user.aiChatData.usage || null,
								loading: false
							};
						}
					}
					setAiChatStates(newStates);
				} else {
					// Reset AI chat states when fetching new users list if no data in response
					setAiChatStates({});
				}
			}
			setLoading(false);
		} else {
			let re = await axios.get(`/api/user/${accountIdFromHook}`).catch((e) => e);
			const newUsers = re.data || [];
			setUsers(newUsers);
			// Process AI Chat data immediately if available in users array
			if (canManageAiChat && newUsers.length > 0) {
				const hasAiChatData = newUsers.some(user => user.aiChatData);
				if (hasAiChatData) {
					const newStates = {};
					for (const user of newUsers) {
						if (user.aiChatData) {
							newStates[user._id] = {
								hasAccess: user.aiChatData.hasAccess || false,
								aiChatEnabled: user.aiChatData.aiChatEnabled,
								customLimit: user.aiChatData.aiChatLimit,
								usage: user.aiChatData.usage || null,
								loading: false
							};
						}
					}
					setAiChatStates(newStates);
				} else {
					// Reset AI chat states when fetching new users list if no data in response
					setAiChatStates({});
				}
			}
			setLoading(false);
		}
	};

	useEffect(() => {
		const search = new URLSearchParams(location.search);
		// eslint-disable-next-line
		setShowAdd(search.get("showAdd") == "true");
		getUsers();
		// eslint-disable-next-line
	}, []);

	const deleteUser = async (id) => {
		try {
			await api.delete(`/api/user/${id}`);
			const newData = users.filter((u) => u._id !== id);
			setUsers(newData);
		} catch (e) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: e?.response?.data?.message || "Error while update new user.",
					id: "updateUserError",
				},
			});
		}
	};

	const handleResendInvitation = async (userId) => {
		try {
			await api.post(`/api/user/resend-invitation`, { userId });

			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "success",
					label: "Invitation email resent successfully.",
					id: "resendInvitationSuccess",
				},
			});			
		} catch (e) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: e?.response?.data?.message || "Error while resending invitation email.",
					id: "resendInvitationError",
				},
			});
		}
	};

	const handleEdit = useCallback((editUser) => {
		setCurrentEdit(editUser);
		setShowAdd(true);
	}, []);

	const handleClose = useCallback(() => {
		setShowAdd(false);
		setCurrentEdit(null);
	}, []);

	const handleAddUser = useCallback(() => {
		setCurrentEdit(null);
		setShowAdd(true);
	}, []);

	// Pause date functionality
	const handlePauseUser = useCallback((user, pauseType = "user") => {
		if (!canPauseUsers) return;
		setPauseDialogState({
			open: true,
			userId: user._id,
			userName: user.fullName,
			pauseDate: pauseType === "user" && user.pauseDate ? user.pauseDate : null,
			pauseType: pauseType
		});
	}, [canPauseUsers]);

	const handlePauseDateSave = async (pauseDate) => {
		setPauseDialogLoading(true);
		try {
			// Only call API for user-wise pause
			if (pauseDialogState.pauseType === "user") {
				const updateData = {
					pauseDate: pauseDate,
					id: pauseDialogState.userId
				};
				const res = await updateUserPauseDate(updateData);
				if (res) {
					setUsers((prevUsers) => prevUsers.map(user => user._id === pauseDialogState.userId ? { ...user, pauseDate: pauseDate } : user));
				}
			} else {
				// For account-wise and facility-wise pauses, pass the date to parent component
				if (onPauseDateSelected) {
					onPauseDateSelected(pauseDialogState.pauseType, pauseDate, pauseDialogState.userId);
				}
			}
		} catch (error) {
			// Error handling is already done in updateOneUser function
		} finally {
			setPauseDialogLoading(false);
		}
	};

	const handlePauseDialogClose = () => {
		setPauseDialogState({ open: false, userId: null, userName: "", pauseDate: null, pauseType: "user" });
	};

	const getFacilities = async () => {
		await getFacilityOption().then((res) => {
			if (res && res.length > 0) {
				setFacilities(res[0]["facilities"]);
			}
		});
	};

	useEffect(() => {
		if (!auth?.accountId) return;
		getFacilities();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth]);

	// Load AI Chat access status for all users when component mounts or users list is fetched
	// If logged user is SUPER or TOTAL, data is already included in users array from API
	useEffect(() => {
		if (canManageAiChat && users.length > 0 && Object.keys(aiChatStates).length === 0) {
			loadAiChatAccessForAllUsers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [users.length, canManageAiChat]); // Use users.length instead of users to avoid re-triggering on user updates

	const loadAiChatAccessForAllUsers = async () => {
		// Check if AI Chat data is already included in users array (from backend)
		const firstUser = users[0];
		if (firstUser && firstUser.aiChatData) {
			// Data is already in users array, use it directly
			const newStates = {};
			for (const user of users) {
				if (user.aiChatData) {
					newStates[user._id] = {
						hasAccess: user.aiChatData.hasAccess || false,
						aiChatEnabled: user.aiChatData.aiChatEnabled,
						customLimit: user.aiChatData.aiChatLimit,
						usage: user.aiChatData.usage || null,
						loading: false
					};
				}
			}
			setAiChatStates(newStates);
		} else {
			// Fallback: Fetch data individually if not included in API response
			const newStates = {};
			for (const user of users) {
				const isSuperOrTotal = [AUTH_ROLES.SUPER, AUTH_ROLES.TOTAL].some(
					role => role.toUpperCase() === user?.role?.slug?.toUpperCase()
				);

				if (!isSuperOrTotal) {
					continue;
				}

				try {
					const accessData = await checkAiChatAccess(user._id);
					const usageData = await getUserUsageStats(user._id);
					newStates[user._id] = {
						hasAccess: accessData?.data?.hasAccess || false,
						aiChatEnabled: accessData?.data?.aiChatEnabled,
						customLimit: accessData?.data?.aiChatLimit,
						usage: usageData?.data?.usage || null,
						loading: false
					};
				} catch (error) {
					newStates[user._id] = {
						hasAccess: false,
						loading: false,
						error: error.message
					};
				}
			}
			setAiChatStates(newStates);
		}
	};

	const handleAiChatToggle = async (userId, enabled) => {
		try {
			setAiChatStates(prev => ({
				...prev,
				[userId]: { ...prev[userId], loading: true }
			}));

			const response = await updateAiChatAccess(userId, enabled);

			if (response?.success) {
				setAiChatStates(prev => ({
					...prev,
					[userId]: {
						...prev[userId],
						hasAccess: enabled,
						aiChatEnabled: enabled,
						loading: false
					}
				}));

				// Update user in list without triggering useEffect (only update aiChatEnabled field)
				setUsers(prevUsers =>
					prevUsers.map(user =>
						user._id === userId
							? { ...user, aiChatEnabled: enabled }
							: user
					)
				);

				store.dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "success",
						label: `AI Chat ${enabled ? 'enabled' : 'disabled'} successfully`,
						id: "aiChatAccessUpdate",
					},
				});
			}
		} catch (error) {
			setAiChatStates(prev => ({
				...prev,
				[userId]: { ...prev[userId], loading: false }
			}));
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: error?.response?.data?.error || "Error updating AI Chat access",
					id: "aiChatAccessError",
				},
			});
		}
	};

	const handleOpenLimitDialog = (user) => {
		const state = aiChatStates[user._id];
		setAiChatLimitDialog({
			open: true,
			userId: user._id,
			userName: user.fullName,
			currentLimit: state?.customLimit || null,
			newLimit: state?.customLimit?.toString() || ""
		});
	};

	const handleCloseLimitDialog = () => {
		setAiChatLimitDialog({
			open: false,
			userId: null,
			userName: "",
			currentLimit: null,
			newLimit: ""
		});
	};

	const handleSaveLimit = async () => {
		const { userId, newLimit } = aiChatLimitDialog;
		try {
			const limitValue = newLimit.trim() === "" ? null : parseInt(newLimit.trim());

			if (limitValue !== null && (isNaN(limitValue) || limitValue < 0)) {
				store.dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "error",
						label: "Please enter a valid number (0 or greater)",
						id: "aiChatLimitError",
					},
				});
				return;
			}

			const response = await setCustomPromptLimit(userId, limitValue);

			if (response?.success) {
				setAiChatStates(prev => ({
					...prev,
					[userId]: {
						...prev[userId],
						customLimit: limitValue
					}
				}));

				// Refresh usage stats
				const usageData = await getUserUsageStats(userId);
				setAiChatStates(prev => ({
					...prev,
					[userId]: {
						...prev[userId],
						usage: usageData?.data?.usage || null
					}
				}));

				store.dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "success",
						label: `Custom limit ${limitValue === null ? 'removed' : 'set'} successfully`,
						id: "aiChatLimitSuccess",
					},
				});

				handleCloseLimitDialog();
			}
		} catch (error) {
			store.dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: error?.response?.data?.error || "Error setting custom limit",
					id: "aiChatLimitError",
				},
			});
		}
	};

	const highlightText = (text, highlight) => {
		if (!highlight) return text;
		const parts = text.split(new RegExp(`(${highlight})`, "gi"));
		return parts.map((part, index) =>
			part.toLowerCase() === highlight.toLowerCase() ? (
				<span key={index} style={{ backgroundColor: "yellow" }}>{part}</span>
			) : (
				part
			)
		);
	};

	const filteredUsers = users.filter((user) =>
		[user.fullName, user.email].some(field =>
			field?.toLowerCase().includes(searchTerm.trim().toLowerCase())
		)
	);

	return (
		<div className={styles.accountUsers}>
			<div className={`df ${styles.hdr}`}>
				<div className={`mla df`}>
					<TextField
						variant="outlined"
						label="Search users"
						value={searchTerm}
						size="medium"
						sx={{
							mr: 2,
							"& .MuiOutlinedInput-root": {
								borderRadius: "8px",
								height: "40px",
								width: "320px"
							},
							"& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
								marginTop: "-5px",
							}
						}}
						InputProps={{
							endAdornment: searchTerm && (
								<InputAdornment position="end">
									<IconButton onClick={() => setSearchTerm("")} size="small">
										<ClearIcon />
									</IconButton>
								</InputAdornment>
							),
						}}
						onChange={(e) => setSearchTerm(e.target.value)}
						className={`ffml fs16 mr-2`}
					/>
					<div className={`inputWrpr ${styles.facilitySelect}`}>
						<select
							className={`ffml fs16 mr-2`}
							value={facilityFilter}
							onChange={(e) => {
								setFacilityFilter(e.target.value);
								getUsers(e.target.value);
							}}
						>
							<option value="">Select All</option>
							{facilities &&
								facilities.length > 0 &&
								facilities.map((item, index) => (
									<option key={index} value={item._id}>
										{item.name}
									</option>
								))}
						</select>
					</div>
					<ShowForPermission type="addUser">
						<Button action={handleAddUser} buttonStyle="theme" text="Add User" size="medium" />
						{showAdd && <AddUser currentEdit={currentEdit} close={handleClose} roles={roles} refreshData={getUsers} />}
					</ShowForPermission>
				</div>
			</div>
			<div className={styles.listWrpr}>
				{loading ? (
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}>
						<CircularProgress />
					</div>
				) : (
					<>
						{filteredUsers.length > 0 &&
							filteredUsers.map((user, index) => (
								<div key={index} className={`df aic ffmm fs14 ${styles.line}`}>
									<h2 style={{ width: "20%" }}>
										{highlightText(user.fullName, searchTerm?.trim())}
										{user.pauseDate && (
											<span style={{
												marginLeft: "8px",
												color: "#F93B5F",
												fontSize: "12px",
												fontWeight: "normal"
											}}>
												⏸️ Paused
											</span>
										)}
									</h2>
									<h3 style={{ width: "40%" }}>{highlightText(user.email, searchTerm?.trim())}</h3>

									<h3 className={`m-l-20`} style={{ width: "10%" }}>
										{user.role?.name}
									</h3>
									{canManageAiChat && [AUTH_ROLES.SUPER, AUTH_ROLES.TOTAL].some(
										role => role.toUpperCase() === auth?.role?.slug?.toUpperCase()
									) && [AUTH_ROLES.SUPER, AUTH_ROLES.TOTAL].some(
										role => role.toUpperCase() === auth?.role?.slug?.toUpperCase()
									) && (
											<div style={{ width: "15%", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", minHeight: "40px", justifyContent: "center" }}>
												{aiChatStates[user._id] && (
													<>
														<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
															<Switch
																size="small"
																checked={aiChatStates[user._id]?.hasAccess || false}
																onChange={(e) => handleAiChatToggle(user._id, e.target.checked)}
																disabled={aiChatStates[user._id]?.loading}
															/>
															<span
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	handleOpenLimitDialog(user);
																}}
																style={{
																	cursor: "pointer",
																	color: "#4879f5",
																	textDecoration: "underline",
																	fontSize: "14px",
																	marginLeft: "4px"
																}}
															>
																AI Chat
															</span>
														</div>
														{aiChatStates[user._id]?.usage && (
															<Tooltip title={`Used: ${aiChatStates[user._id].usage.used || 0} / ${aiChatStates[user._id].usage.limit || 150} (${aiChatStates[user._id].usage.remaining !== undefined ? aiChatStates[user._id].usage.remaining : (aiChatStates[user._id].usage.limit || 150)} remaining)`}>
																<span style={{ fontSize: "11px", color: "#666", cursor: "pointer" }}>
																	Usage: {aiChatStates[user._id].usage.used || 0}/{aiChatStates[user._id].usage.limit || 150}
																</span>
															</Tooltip>
														)}
														{aiChatStates[user._id]?.customLimit !== null && aiChatStates[user._id]?.customLimit !== undefined && (
															<span style={{ fontSize: "11px", color: "#4879f5" }}>
																Custom: {aiChatStates[user._id].customLimit}
															</span>
														)}
													</>
												)}
											</div>
										)}
									<div className={`mla df aic`} style={{ width: canManageAiChat ? "25%" : "auto" }}>
										<ShowForPermission type="editUser">
											<div className={`m-r-10 c-pointer`} onClick={() => handleEdit(user)} style={{ marginBotton: "5px" }}>
												<EditSVG fill="#4879f5" />
											</div>
										</ShowForPermission>
										{canPauseUsers && (
											<div
												className={`m-r-10 c-pointer`}
												onClick={() => handlePauseUser(user)}
												style={{ marginRight: "10px" }}
												title={user.pauseDate ? "Update Pause Date" : "Set Pause Date"}
											>
												<PauseIcon style={{
													color: user.pauseDate ? "#F93B5F" : "#4879f5",
													fontSize: "20px"
												}} />
											</div>
										)}

										<IconButton
											size="small"											
											onClick={(e) => {
												e.stopPropagation();
												setActionMenuState({ anchorEl: e.currentTarget, user });
											}}
										>
											<MoreVertIcon fontSize="small" />
										</IconButton>

									</div>
								</div>
							))}
						{filteredUsers.length === 0 && !loading && <div key={"no-data-user"} className={`df aic ffmm fs14 ${styles.line}`}>No data found</div>}

						<Menu
							anchorEl={actionMenuState.anchorEl}
							open={Boolean(actionMenuState.anchorEl)}
							onClose={() => setActionMenuState({ anchorEl: null, user: null })}
							anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							transformOrigin={{ vertical: "top", horizontal: "right" }}
							PaperProps={{
								style: {
									minWidth: '200px',
								}
							}}
						>
							<ShowForPermission type="editUser">
								<MenuItem
									onClick={() => {
										const selectedUser = actionMenuState.user;
										setActionMenuState({ anchorEl: null, user: null });
										if (selectedUser?._id) {
											navigate("/logs", {
												state: {
													userId: selectedUser._id,
													userName: selectedUser.fullName,
													accountName: common?.selectedAccount?.name
												}
											});
										}
									}}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '12px',
										padding: '10px 16px',
										'&:hover': {
											backgroundColor: '#f5f5f5',
										}
									}}
								>
									<TimesheetSVG fill="#4879f5" style={{ width: 20, height: 20 }} />
									<span style={{ fontSize: '14px', color: '#333' }}>Time log</span>
								</MenuItem>
							</ShowForPermission>
							<ShowForPermission type="deleteUser">
								<MenuItem
									onClick={() => {
										const selectedUser = actionMenuState.user;
										setActionMenuState({ anchorEl: null, user: null });
										if (selectedUser?._id) {
											setDeleteUserState({ state: true, userId: selectedUser._id });
										}
									}}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '12px',
										padding: '10px 16px',
										'&:hover': {
											backgroundColor: '#f5f5f5',
										}
									}}
								>
									<DeleteSVG fill="#F93B5F" style={{ width: 20, height: 20 }} />
									<span style={{ fontSize: '14px', color: '#F93B5F' }}>Delete</span>
								</MenuItem>
							</ShowForPermission>
							<MenuItem
								onClick={async () => {
									const selectedUser = actionMenuState.user;
									setActionMenuState({ anchorEl: null, user: null });
									if (selectedUser?._id) {
										try {
											await confirm({
												title: 'Resend Invitation Email',
												description: `Are you sure you want to resend the invitation email to ${selectedUser.fullName || selectedUser.email}?<br/><br/>Note: A new password will be generated and sent to the user. Their current password will no longer work.`,
												yesText: 'Yes, Resend',
												noText: 'Cancel'
											});
											await handleResendInvitation(selectedUser._id);
										} catch {
											// User cancelled, do nothing
										}
									}
								}}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '12px',
									padding: '10px 16px',
									'&:hover': {
										backgroundColor: '#f5f5f5',
									}
								}}
							>
								<EmailIcon sx={{ fontSize: 20, color: '#4879f5' }} />
								<span style={{ fontSize: '14px', color: '#333' }}>Resend invitation email</span>
							</MenuItem>
						</Menu>
					</>
				)}
			</div>
			<PopupDialog
				state={deleteUserState.state}
				isDestructiveAction={true}
				isLoading={deleteUserLoading}
				onClose={() => setDeleteUserState({ state: false, accountId: null })}
				onConfirm={async () => {
					setDeleteUserLoading(true);
					await deleteUser(deleteUserState.userId);
					setDeleteUserLoading(false);
					setDeleteUserState({ state: false, userId: null });
				}}
				dialogMessage={"Are you sure you want to delete this user?"}
				confirmBtnText={"Delete"}
			/>

			{/* Pause Date Dialog */}
			<PauseDateDialog
				open={pauseDialogState.open}
				onClose={handlePauseDialogClose}
				onSave={handlePauseDateSave}
				title={`Set ${pauseDialogState.pauseType === "user" ? "User" : pauseDialogState.pauseType === "account" ? "Account" : "Facility"} Pause Date for ${pauseDialogState.userName}`}
				initialDate={pauseDialogState.pauseDate}
				loading={pauseDialogLoading}
				showSuccessNotification={pauseDialogState.pauseType !== "user"}
			/>

			{/* AI Chat Limit Dialog */}
			<Dialog open={aiChatLimitDialog.open} onClose={handleCloseLimitDialog}>
				<DialogTitle>Set Custom Prompt Limit for {aiChatLimitDialog.userName}</DialogTitle>
				<DialogContent>
					<div style={{ marginTop: "16px" }}>
						<TextField
							fullWidth
							label="Monthly Prompt Limit"
							type="number"
							value={aiChatLimitDialog.newLimit}
							onChange={(e) => setAiChatLimitDialog(prev => ({ ...prev, newLimit: e.target.value }))}
							placeholder="Leave empty to use default (150)"
							helperText={aiChatLimitDialog.currentLimit ? `Current limit: ${aiChatLimitDialog.currentLimit}. Leave empty to remove custom limit.` : "Default limit is 150 prompts per month. Leave empty to use default."}
							inputProps={{ min: 0 }}
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button action={handleCloseLimitDialog} buttonStyle="secondary" text="Cancel" size="medium" />
					<Button action={handleSaveLimit} buttonStyle="theme" text="Save" size="medium" />
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default AccountUserPage;